<script setup>
import { ref, provide, onMounted, defineAsyncComponent, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import RoutedView from '@/components/RoutedView.vue'
import { createClient, processLock } from '@supabase/supabase-js'
import { boundedAuthFetch } from './auth/boundedAuthFetch'
import { lastDashboardPath, FIRST_SCREEN_ROUTE_NAMES } from './router'
import { firstSound } from './playback/firstSoundSignal'
import { createProgressStore, createSessionStore } from '@ssi/core'
import { catalogueStampOf, catalogueStampOfRows, decideCatalogueRead } from './utils/catalogueStamp'
import { pickFirstOpenDefaultCourse } from './containers/firstOpenDefaultCourse'
import { createCourseDataProvider } from './providers/CourseDataProvider'
import { loadConfig, isSupabaseConfigured, missingRequiredConfig } from './config/env'
import { shouldOfferAppInstall } from './platform/capabilities'
import { listenForStoreTransactions } from './platform/storeBilling'
import { handbookPath } from './walkthrough/handbookLink'
import { useAuth } from './composables/useAuth'
import { usePlayerLog } from './composables/usePlayerLog'
import { startInstallTelemetry } from './utils/installTelemetry'
import {
  prewarmInstantCaches,
  setInstantPlaybackAuthProvider,
  isBundleBootstrapEnabled,
} from './composables/useInstantPlayback'
import { pickerFirstActive, PICKER_FIRST_PATH } from './platform/pickerFirst'
import { requestCoursePicker, linkedCourseToSave, unknownCourseLanding, playCourseFromAddress } from './platform/learnLink'
import { resolveLinkedCourse, mayPreviewOutsideCatalogueAwaiting, courseRowQuery, enrolledCodesForCourse, isDashboardUserRpc } from './platform/hiddenCourseLink'
import { useI18n } from './composables/useI18n'
// Static, not async: it carries the boot loading line, which must be up as
// soon as the shell is (job #831 — it replaced the boot TypedDialog).
import PwaUpdatePrompt from './components/PwaUpdatePrompt.vue'
import { setCourseBundleAuthProvider, setCourseBundleIdentityProvider, setCourseBundleEntitlementProvider, revalidateCachedBundles, getCourseBundle, bootBundleHave } from './composables/useCourseBundle'
import { startSignedInBoot, restartSignedInBoot } from './playback/signedInBoot'
import { runResetRecovery } from './utils/resetRecovery'
import { checkKillSwitch, unregisterAllServiceWorkers, clearAllCaches, killSwitchMessage } from './composables/useServiceWorkerSafety'
import { runOpenUpdateGate } from './composables/useOpenUpdateGate'
import { confirmAfterReload, startUpdateWatch } from './composables/useAppUpdate'
import { useTheme } from './composables/useTheme'
import { useEagerScriptPreload } from './composables/useEagerScriptPreload'
import { checkContentVersion } from './composables/useScriptCache'
// A shared Script-mode link names a TARGET LANGUAGE; these two resolve it to
// the course that language's ladder is read from (job #672).
import { parseScriptShare } from './script/shareParam'
import { staleBuildKeys } from './cache/staleBuildKeys'
import { useInviteCode } from './composables/useInviteCode'
import { useAccessClaim } from './composables/useAccessClaim'
import { useSharedUserEntitlements } from './composables/useUserEntitlements'
import { hasTryEntitlement } from './composables/useEntitlement'
import { useSharedSubscription } from './composables/useSubscription'
import { useFamilyModal } from './composables/useFamilyModal'
import { usePendingPurchase } from './composables/usePendingPurchase'
import { useOfflineLease } from './composables/useOfflineLease'
import {
  withNetworkTimeout,
  NETWORK_TIMEOUT,
  BACKGROUND_FETCH_TIMEOUT_MS,
  wireNetworkRecovery,
} from './config/networkGate'
import { waitForCatalogue, usableCatalogue } from './config/catalogueWait'
import { checkCourseAccess, inferPricingTier } from '@ssi/core'
import { useUserRole } from './composables/useUserRole'
import { installConsoleDedup } from './utils/consoleDedup'
import { isEmbedContext } from './platform/embedMode'
import {
  rememberCourse,
  readRememberedCourse,
  rememberedCourseWasAutoAssigned,
} from './platform/courseChoice'
// Async-load the 4 always-mounted overlay components — none of them
// render anything visible until some internal condition triggers
// (PWA update available, install prompt eligible, admin flag), so
// they don't belong on the first-paint critical path.
// The on-open update screen. Renders nothing unless useOpenUpdateGate proves
// the live build is newer than this one — see that composable's header for why
// it is allowed to hold the surface when the banner is not.
const UpdateOnOpenOverlay = defineAsyncComponent(() => import('./components/UpdateOnOpenOverlay.vue'))
// "Was the earlier sign-in you?" — the contest card (job #371). App-level so a
// code sign-in on any screen is covered; see src/auth/claimAccount.ts.
const AccountContestPrompt = defineAsyncComponent(() => import('./components/auth/AccountContestPrompt.vue'))
const InstallBanner = defineAsyncComponent(() => import('./components/InstallBanner.vue'))
const ClaimRefusedBanner = defineAsyncComponent(() => import('./components/ClaimRefusedBanner.vue'))
const GrantProofPrompt = defineAsyncComponent(() => import('./components/GrantProofPrompt.vue'))
const ViewingAsBanner = defineAsyncComponent(() => import('./components/ViewingAsBanner.vue'))
// Walkthrough overlay — renders nothing until a walk is started by a user tap
// (noticing invitation / How-this-works "Show me"); never auto-plays. Mounted
// only while a walk is active (job #850): mounting it unconditionally fetched
// the overlay AND the whole compiled pack on every boot, 62 kB gz sharing a
// stranger's pipe with the player before the first word.
const WalkOverlay = defineAsyncComponent(() => import('./components/admin/WalkOverlay.vue'))
import { activeWalk } from './walkthrough/walkState'
import { setSchoolsClient } from './composables/schools/client'
import { useViewAs } from './composables/useViewAs'
import { installViewAsFetchGuard } from './composables/viewAsFetchGuard'
import AppEscape from './components/AppEscape.vue'
import MustChangePassword from './components/auth/MustChangePassword.vue'
import { barShowsBack } from './nav/learnerBar'
import RoutedBottomNav from './components/RoutedBottomNav.vue'
import PlayingAsYourselfBanner from './components/schools/PlayingAsYourselfBanner.vue'
import PlaySwitchesChip from './components/PlaySwitchesChip.vue'
import { isPlayCopy } from './play/switches'
import { storageKeys } from './platform/safeStorage'
import { watchGameAttribution } from './composables/useGameAttribution'
import { watchGoSignIn } from './composables/useGoLink'
import { tidyCountingKeys } from './composables/countingConsent'
import { watchCampaignFunnel } from './platform/campaignArrival'
const showPlaySwitches = isPlayCopy()
// The purchase surfaces (job #763): still mounted on every page exactly as
// before and still reading the same shared checkout/subscription state, but
// fetched as their own chunks right after first paint instead of riding the
// entry. Statically imported they put the Paddle SDK, the checkout composable
// and the family manager in front of every stranger's first word.
const CheckoutOverlay = defineAsyncComponent(() => import('./components/CheckoutOverlay.vue'))
const PurchasePendingOverlay = defineAsyncComponent(() => import('./components/PurchasePendingOverlay.vue'))
const StoreConfirmingOverlay = defineAsyncComponent(() => import('./components/StoreConfirmingOverlay.vue'))
const PlanPicker = defineAsyncComponent(() => import('./components/PlanPicker.vue'))
// The pupil's £5 goes through a parent (job #102): no card form for a pupil.
const ParentAskOverlay = defineAsyncComponent(() => import('./components/ParentAskOverlay.vue'))
const FamilyManagementModal = defineAsyncComponent(() => import('./components/FamilyManagementModal.vue'))
// In-app browser — renders nothing until a link asks to open a page inside the
// app rather than throwing the learner out to a browser tab.
const InAppBrowser = defineAsyncComponent(() => import('./components/InAppBrowser.vue'))

// THE FRAMED DEMO BOOTS NOTHING. `/embed/*` is a marketing visitor's first
// second on a phone, inside somebody else's page: no sign-in, no entitlement
// round-trips, no multi-megabyte bundle warm-up, no service worker, nothing
// written to storage on an origin they never chose to hold anything for. Every
// eager side effect in this file asks this first. See platform/embedMode.ts.
const IS_EMBED = isEmbedContext()

// Suppress consecutive identical console errors/warnings after 3 repeats
installConsoleDedup()
// Clear the "network is stalled" observation the moment the browser reports a
// reconnect, so a learner walking back into signal gets live content again
// without waiting out the TTL.
wireNetworkRecovery()

// "No dead ends": show the shell-level escape on any route that doesn't carry
// its own way out. The immersive player and the shelled containers (schools /
// teach / admin) opt out via meta.hideAppEscape; everything else (bare
// top-level pages like onboarding, /with/:code, /teacher-insights) gets it.
// Critical in the installed PWA, which has no browser back button.
const route = useRoute()

// OVERLAYS THAT WAIT FOR THE FIRST SOUND (job #850). The purchase surfaces,
// the family manager, the in-app browser, the install banner, the tester
// widget and the view-as band all render nothing at boot, but mounting them
// fetched ~35 kB of chunks that shared a stranger's pipe with the player
// before the first word. They now mount at the first of: the first sound;
// any tap or key press anywhere (pointerdown lands before the click that
// would open one, and their open-state lives in shared composables they read
// on mount); any route that is not a first screen; or their own reason to
// show already being true (a returning Paddle purchase, a family link, a
// staff member viewing as someone). The on-open update gate and the account
// contest card stay eager: they must be able to hold the surface before play.
const overlaysMounted = ref(false)
function mountOverlays() {
  if (overlaysMounted.value) return
  overlaysMounted.value = true
  window.removeEventListener('pointerdown', mountOverlays, true)
  window.removeEventListener('keydown', mountOverlays, true)
}
if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', mountOverlays, true)
  window.addEventListener('keydown', mountOverlays, true)
}
void firstSound().then(mountOverlays)
watch(() => route.name, (name) => {
  if (name && !FIRST_SCREEN_ROUTE_NAMES.includes(name)) mountOverlays()
}, { immediate: true })
const pendingPurchaseOverlay = usePendingPurchase().showOverlay
const { isViewingAs: viewingAsSomeone } = useUserRole()
const router = useRouter()
onMounted(() => {
  document.addEventListener('click', (event) => {
    const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null
    const path = anchor && handbookPath(anchor.getAttribute('href') || '')
    if (!path || event.defaultPrevented) return
    event.preventDefault()
    void router.push(path)
  }, true)
})

// Pages scroll by default; a route that pins itself to the viewport (the
// player) sets meta.scrollLock and the document stops panning (style.css).
watch(() => route.meta?.scrollLock, (lock) => {
  document.documentElement.classList.toggle('scroll-lock', !!lock)
}, { immediate: true })
// The first navigation to the player or the first-load picker is pending:
// the router has matched nothing yet because the route's code is still
// downloading (job #784). Read once from the path, which is where that
// navigation is going.
const { t } = useI18n()
const bootPath = typeof window !== 'undefined' ? window.location.pathname : ''
const bootingToPlayer = computed(() => !IS_EMBED && route.matched.length === 0 && (bootPath === '/' || bootPath === PICKER_FIRST_PATH))
// A stranger is bound for Choose Your Course, so the line says what is coming:
// the courses. It stays up until the list itself is on screen (job #239).
const bootingToPicker = computed(() => pickerFirstActive.value && (route.matched.length === 0 || enrolledCourses.value.length === 0))
const bootDialogLine = computed(() => pickerFirstActive.value ? t('courseSelector.loadingCourses') : t('loading.phrasesReady'))
const showAppEscape = computed(() => !route.matched.some((r) => r.meta?.hideAppEscape))
const showLearnerNav = computed(() => route.matched.some((r) => r.meta?.learnerNav)
  || (route.name === 'schools-handbook' && route.query.appHandbook === '1'))

// The immersive player opts out of AppEscape above — a plain learner sees no
// chrome at all, which is the point. But since the owner ruling of 2026-08-06
// ("entering the player always gives the navless player"), staff self-practice
// lands here too, with no shell nav to get home. For those users only — the ones
// carrying a `ssi-last-dashboard` breadcrumb — show the same low-emphasis pill
// pointed at their own surface, so they aren't stranded.
// It steps aside while the player's bar shows its back arrow — a mode, the
// Library or Settings is up — so no screen offers two ways back at once
// (job #48, Tom 2026-09-24: one way back).
const dashboardEscape = computed(() =>
  route.name === 'player' && !barShowsBack.value ? lastDashboardPath() : null,
)

// RedeemCode.vue (mounted at /redeem and /group) owns the pendingCode it
// creates and drives its own inline auth/details UI — the global sign-in
// modal must stay closed while either is active (see onMounted below).
const isRedeemFlowRoute = computed(() => route.name === 'redeem-code' || route.name === 'group-landing')

// RECOVERY MODE: If ?reset=1 in URL, clear everything and reload
// This helps users stuck in broken states
runResetRecovery({ win: window, unregisterAllServiceWorkers, clearAllCaches })

// DEMO CLEANUP: Remove stale demo state from localStorage
// This catches cases where someone visited /demo, started a demo,
// then closed the tab without stopping it properly
if (localStorage.getItem('ssi-dev-tier') === 'paid' && !sessionStorage.getItem('ssi-demo-active')) {
  console.log('[App] Cleaning up stale demo state from localStorage')
  localStorage.removeItem('ssi-dev-tier')
  localStorage.removeItem('ssi-active-class')
  // Don't remove ssi-last-course — user might have set that themselves.
}

// Wipe dead god-mode storage keys — impersonation was removed from the
// app. These only exist on browsers that ran a prior version; safe to
// drop unconditionally every boot until enough users have rotated.
localStorage.removeItem('ssi-god-mode-user')
sessionStorage.removeItem('ssi-god-mode-user')
localStorage.removeItem('ssi-god-fab-pos')

// One-shot flag SettingsScreen.vue sets immediately before a reset/
// recover reload, to stop LearningPlayer's pagehide dormancy flush from
// re-saving the pre-reset position during that reload. sessionStorage
// survives the reload itself (same tab), so it must be cleared here, at
// the start of the FRESH boot, or it would silently suspend every
// position save for the rest of the session.
sessionStorage.removeItem('ssi-position-writes-suspended')

// Initialize theme (reads from localStorage, applies to document)
const { theme, toggleTheme, setTheme } = useTheme()

// Build version injected by Vite at build time
// @ts-ignore - __BUILD_NUMBER__ is defined by Vite
const BUILD_VERSION = typeof __BUILD_NUMBER__ !== 'undefined' ? __BUILD_NUMBER__ : 'dev'

/**
 * Cache invalidation on deploy
 * Clears stale script caches when a new build is deployed
 */
/** Returns the build this device ran last, before it is overwritten — the
 * update line uses it to say "Updated to …" after a plain relaunch. */
const invalidateStaleCaches = () => {
  const CACHE_VERSION_KEY = 'ssi-build-version'

  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY)

  if (storedVersion !== BUILD_VERSION) {
    // WHICH KEYS A BUILD CHANGE MAY TAKE — cache/staleBuildKeys.ts, which
    // carries the rule and the reason. The short version: the legacy
    // localStorage copies of the LEARNING SCRIPT, and nothing else. Script
    // MODE's own marks (calibration, introductions, layers announced, the
    // ladder mirror) share the old `ssi-script-` prefix and must survive a
    // deploy — wiping them sent a returning learner back through the pen
    // calibration (job #652).
    //
    // Position caches (ssi_learning_position_*, ssi_explorer_position_*) are
    // deliberately NOT cleared here either — they're stored as absolute ids
    // precisely so they survive a deploy/script regeneration (see
    // savePositionToLocalStorage's comment), and wiping them on every build
    // silently restarted every guest at round 1 (guests have no server row to
    // fall back to). Position authority ruling
    // (archive/docs-retired-2026-08-24/pwa-lifecycle-design.md §2.3) — only
    // learner intent (reset, sign-out) may clear a position key.
    const keysToRemove = staleBuildKeys(localStorage)

    // Now remove them
    keysToRemove.forEach(key => localStorage.removeItem(key))

    // Clear SW runtime caches on deploy — but PRESERVE ssi-audio-cache.
    //
    // 2026-05-23: audio cache wipe-on-deploy was hammering learners on
    // slow networks. Every deploy → audio cache nuked → every <audio>
    // play = network round-trip via SW CacheFirst miss. Tom's stress
    // test showed 57,859 requests / 589 MB transferred over 4 min on
    // Slow 4G after a single "Update" tap.
    //
    // Why the wipe was wrong:
    //  - Audio UUIDs are content-addressed (UUID = hash of text+role),
    //    so "wrong audio under same UUID" can't happen
    //  - Workbox config has cacheableResponse:{statuses:[200]} so 404s
    //    are never cached in the first place
    //  - When audio IS regenerated (rare), `checkContentVersion()` in
    //    useScriptCache fires `caches.delete(AUDIO_CACHE_NAME)` for
    //    just that course's transition — the right scoped trigger
    //
    // Navigation/font caches still get wiped — those benefit from a
    // fresh fetch on deploy and are tiny.
    if ('caches' in window) {
      // ssi-auth-handoff carries the session across the iOS Safari → Home
      // Screen PWA boundary (see utils/authHandoff.ts) — wiping it on
      // deploy would silently break the install sign-in carry-over.
      // PREFIX match, not exact: the real workbox precache is named
      // "workbox-precache-v2-<origin>/", so the old exact-match Set deleted
      // the ENTIRE app-shell precache on every deploy — leaving offline cold
      // start on the browser "No internet" page until the next SW update
      // reinstalled it (found 2026-07-31 chasing the always-play invariant).
      // ssi-assets-cache holds the content-hashed chunks the SW no longer
      // precaches (job #844); a hashed name can never be stale, and wiping it
      // here would strand an offline learner on any lazy screen after an update.
      const PRESERVE_PREFIXES = ['workbox-precache', 'ssi-audio-cache', 'ssi-auth-handoff', 'ssi-assets-cache']
      caches.keys().then(names => {
        const cleared = names.filter(n => !PRESERVE_PREFIXES.some(p => n.startsWith(p)))
        cleared.forEach(name => caches.delete(name))
        if (cleared.length > 0) {
          console.log(`[App] Cleared ${cleared.length} runtime caches:`, cleared)
        }
      }).catch(() => {})
    }

    // Store new version
    localStorage.setItem(CACHE_VERSION_KEY, BUILD_VERSION)
    console.log(`[App] Build ${storedVersion} → ${BUILD_VERSION}, cleared ${keysToRemove.length} cached items`)
  }
  return storedVersion
}

// Load configuration
const config = loadConfig()

// Auth state
const auth = useAuth()
// Set up at setup() level so usePlayerLog's own mount/unmount hooks register (job #783).
const installLog = usePlayerLog({
  learnerId: computed(() => auth.learner.value?.id ?? null),
  clientVersion: BUILD_VERSION,
  getToken: () => auth?.getToken?.() ?? Promise.resolve(null),
})

// Initialize stores (null if database not configured)
const progressStore = ref(null)
const sessionStore = ref(null)
const courseDataProvider = ref(null)
const supabaseClient = ref(null)

// Create Supabase client synchronously (before children mount) so globally-
// mounted components like <GodModePanel> can read the schools-client bridge
// during their own onMounted. Deferring this to App.vue's onMounted meant
// the child's onMounted (which fires first in Vue 3) saw a missing client,
// getSchoolsClient() threw, the error was swallowed, and GOD mode never
// surfaced on non-/schools routes.
// The remembered-course key and its origin marker live in platform/courseChoice
// (imported above), so the early bundle warm-up in the block that follows can
// read them without tripping over the temporal dead zone.

/**
 * Start the bundle download the moment a course can be NAMED — the intent
 * signal, not the Start press (Tom, 2026-08-29: "by the time you get to the
 * play button ... it has already anticipated you would play that song").
 *
 * There are two moments at which the app first learns which course the
 * learner is heading for, and they are seconds apart:
 *
 *  1. Synchronously at boot, from the `?course=` param or the last-played
 *     course in storage. This is the common case and it fires at ~250ms.
 *  2. From the learner's OWN saved preference (`preferences.last_course_code`)
 *     once auth resolves — which is the only signal available on a fresh
 *     device, a fresh PWA install, or after a storage wipe, because there is
 *     nothing in localStorage to read. Measured on dev 2026-08-29, that path
 *     started the bundle at ~1.2s instead of ~0.25s: a second of overlap with
 *     app boot thrown away for want of a name.
 *
 * Both call here. `getCourseBundle` coalesces in flight and caches per
 * session, so calling it twice costs one fetch, and every later consumer
 * (`prewarmInstantCaches`, the player's own bootstrap) JOINS this one rather
 * than starting a second. A wrong guess costs one unused request, which is
 * why it is gated on the bundle flag and only ever fired for a course the
 * learner has actually pointed at.
 */
// The course this visit named at boot (address, demo, or the remembered one),
// if any. The second warm-up below is only for a visit that named none.
let bootIntendedCourse = null

function warmBundleForIntent(courseCode) {
  if (!courseCode) return
  try {
    if (!isBundleBootstrapEnabled(courseCode)) return
    // The whole course waits for the first sound, always (job #290, Tom
    // 2026-10-29: open = position + the first cycle's audio, then play). A
    // copy on the device used to be read back and refreshed here at boot: on
    // Tom's phone that was a 14 MB read and an 11.7 s re-download of Spanish
    // sharing the pipe with the first window. Offline nothing would play any
    // sooner for it: the player reads the stored copy itself when it must.
    void firstSound()
      .then(() => getCourseBundle(courseCode))
      .catch(() => {})
  } catch {
    /* no window (tests / SSR) — the player fetches it later exactly as before */
  }
}

/** processLock, behind index.html's in-flight token refresh when there is one (job #298). */
function bootLock() {
  const early = typeof window !== 'undefined' ? window.__ssiEarlyRefresh : null
  if (!early || typeof early.then !== 'function') return processLock
  // Bounded: a lock another tab never releases must not strand this one.
  const settled = Promise.race([early.then(() => undefined, () => undefined), new Promise((r) => setTimeout(r, 6000))])
  return (name, acquireTimeout, fn) => settled.then(() => processLock(name, acquireTimeout, fn))
}

if (IS_EMBED) {
  // Nothing: no client, no auth providers, no bundle warm-up. The demo talks to
  // exactly one anonymous endpoint and owns that fetch itself.
} else if (config.features.useDatabase && isSupabaseConfigured(config)) {
  try {
    // No boot hold on the client (job #271, Tom 2026-10-16: "Screw the
    // hold-back"). The player's own reads go first because the player asks
    // first; background lanes (pods, Layer 1, meta commentary, adaptation
    // history, the session row) start themselves after firstSound().
    supabaseClient.value = createClient(
      config.supabase.url,
      config.supabase.anonKey,
      // lock (job #298). supabase-js's default in a browser is navigator.locks,
      // and every getSession() takes it. Measured in WebKit (the iPhone's
      // engine) on a signed-in first open: ~200 ms to GRANT an uncontended
      // lock, and the boot's half-dozen getSession() calls queue through it one
      // by one, so the first PostgREST read left at ~744 ms against ~266 ms in
      // Chromium (2-23 ms per grant). An in-process lock costs nothing. What it
      // gives up is cross-tab coordination of the token refresh: two tabs
      // refreshing at once both present the same refresh token, which GoTrue's
      // refresh-token reuse interval (10 s) accepts. When index.html is already
      // refreshing an expired token, the lock waits for that first, so
      // supabase-js reads the new session and never repeats the refresh.
      // global.fetch (job #632): a time limit on /auth/v1/ calls only. A token
      // refresh with no answer held the client's lock, so every getSession()
      // waited behind it and Admin > Games sat on "Loading…" without sending.
      { auth: { persistSession: true, autoRefreshToken: true, lock: bootLock() }, global: { fetch: boundedAuthFetch() } }
    )
    setSchoolsClient(supabaseClient.value)
    // Feed the caller's Supabase access token to the instant-playback fast-path
    // fetches (round-map / cycles / infplay-cycles). These endpoints are
    // entitlement-gated server-side (d4396730): without a token, a signed-in
    // paid learner past the free-preview window (seed <=19) is treated as
    // anonymous and 403'd onto the slow legacy walk. getSession() is a local
    // read (no network), so resolving it per fetch is cheap.
    const accessToken = async () => {
      const client = supabaseClient.value
      if (!client) return null
      try {
        const { data } = await client.auth.getSession()
        return data.session?.access_token ?? null
      } catch {
        return null
      }
    }
    setInstantPlaybackAuthProvider(accessToken)
    // /bundle is gated by the same server-side entitlement resolver, and for
    // the same reason: an anonymous fetch hands a signed-in paid learner the
    // sliced preview bundle instead of the course.
    setCourseBundleAuthProvider(accessToken)
    // ...and who the caller IS, which the token cannot say (it rotates on
    // every refresh). The cached full bundle is only served back to the
    // identity that fetched it (SEC0901-D-02) — same local getSession() read,
    // no extra network.
    setCourseBundleIdentityProvider(async () => {
      const client = supabaseClient.value
      if (!client) return null
      try {
        const { data } = await client.auth.getSession()
        return data.session?.user?.id ?? null
      } catch {
        return null
      }
    })
    // ...and what the caller is ENTITLED to on each course, as the app's own
    // verdict has it (job #778). A cached full bundle is only valid while the
    // identity that fetched it is still entitled: the identity check above
    // cannot see a lapse, so a lapsed subscriber kept the whole course from
    // IndexedDB until the content version moved. Null while the answer is
    // still in flight — never a disagreement — so boot serves the cache as
    // before and the sweep below re-asks once the verdict lands.
    setCourseBundleEntitlementProvider((courseCode) => {
      if (auth.isAuthenticated.value && !useSharedSubscription().hasHydrated.value) return null
      const row = (enrolledCourses.value || []).find((c) => c?.course_code === courseCode)
      const course = row ?? { course_code: courseCode, target_lang: courseCode.split('_')[0] }
      return courseAccessResult(course).canAccess ? 'full' : 'preview'
    })

    // Start the bundle download at the EARLIEST moment we can name a course.
    // Everything that used to kick it off — course-list resolution, the
    // enrollment lookup, the player's own mount — happens well into boot, so
    // on a cold first play the learner waited for the whole multi-megabyte
    // download from THERE. The remembered course (URL param, else last
    // played) is the course the learner lands on nearly every time, so
    // warming it here overlaps the download with the rest of app boot rather
    // than queueing behind it. Fire-and-forget; `getCourseBundle` de-dupes in
    // flight, so `prewarmInstantCaches` and the player's own bootstrap join
    // THIS fetch instead of starting another. A wrong guess costs one unused
    // request, which is why it is gated on the bundle flag.
    // ...but ONLY for a course the visitor actually pointed at. The
    // remembered code can also be one the app assigned to them (App.vue's
    // anonymous fallback), and warming that spent a multi-megabyte guess on
    // every enrol, redeem, join and try-link visit by somebody on their way
    // somewhere else entirely (job #596). No known destination, no warm-up.
    try {
      const deepLinked = playCourseFromAddress(window.location.pathname, window.location.search)
      const demoCourse = sessionStorage.getItem('ssi-demo-last-course')
      const remembered = rememberedCourseWasAutoAssigned() ? null : readRememberedCourse().code
      const intended = deepLinked || demoCourse || remembered
      bootIntendedCourse = intended || null
      // A RETURNING SIGNED-IN LEARNER: one call for everything boot needs to
      // know about them (job #892) — identity, entitlement, subscription,
      // where they are in the course, and the bundle itself when this device
      // does not already hold the current one. Fired BEFORE the warm-up below
      // so the warm-up's bundle request finds it in flight and is answered
      // from it. A stranger has no stored session and skips this entirely.
      if (storageKeys(localStorage).some((k) => /^sb-.+-auth-token$/.test(k))) {
        const bootCourse = intended && isBundleBootstrapEnabled(intended) ? intended : null
        startSignedInBoot({
          course: bootCourse,
          session: async () => {
            const { data } = await supabaseClient.value.auth.getSession()
            const sess = data.session
            return sess?.access_token && sess.user?.id ? { token: sess.access_token, userId: sess.user.id } : null
          },
          have: bootCourse ? (userId) => bootBundleHave(bootCourse, userId) : undefined,
          // What index.html's early boot keys its replay on (job #298).
          intent: intended || null,
        })
      }
      warmBundleForIntent(intended)
    } catch {
      /* no storage, no window — the player fetches it later exactly as before */
    }
  } catch (err) {
    console.error('[App] Failed to initialize Supabase client synchronously:', err)
  }
} else {
  // FAIL LOUDLY. Skipping this block silently is how a single absent build-time
  // variable bricked sign-in for everybody with no trace anywhere: no client is
  // ever created, so every sign-in path answers "App not ready. Please try
  // again." forever. console.error (not log/info/debug — those are stripped in
  // production by the vite esbuild pure list) so the cause is one glance away
  // in a real browser console, and it NAMES the missing variables.
  console.error(
    '[App] NO SUPABASE CLIENT — sign-in, progress and course loading are all dead. ' +
    'Missing build-time config: ' + (missingRequiredConfig(config).join(', ') || 'none reported') +
    '. Set these on the Vercel project and redeploy.',
  )
}

// Eager script preload — provided for consumers that trigger their own walk
// (DemoLauncher) and for LearningPlayer's fallback paths. App.vue itself no
// longer fires it: on the instant-playback path the ONE full course-wide walk
// is LearningPlayer's deferred handoff (which threads the live algorithm
// config — pod pin, L1 fire counts, script shape — that this preload's
// default-config walk never had). Firing it here too just ran the same
// six course-wide queries again, starving the bootstrap that actually
// gets audio playing.
const eagerScript = useEagerScriptPreload()

// The one piece of the old preload that WAS load-bearing at course open:
// checkContentVersion clears the warm-start script cache when the course's
// content_version bumps, and LearningPlayer's cache fast-path assumes that
// check has run BEFORE it mounts (a cache hit means current content). It's a
// single tiny query — run it immediately, not on idle, so the ordering holds.
const checkCourseContentVersion = (client, code) => {
  void checkContentVersion(client, code).catch(() => {}) // offline is fine
}

// Invite code composable (singleton)
const inviteCode = useInviteCode()

// Capture beforeinstallprompt for PWA install guide. Not inside the native
// shell: there is nothing to install from in there, and holding a deferred
// prompt only gives the install surfaces something to light up on. Asked once,
// at the seam (platform/capabilities), not sniffed here.
const installPrompt = ref(null)
if (!IS_EMBED && shouldOfferAppInstall()) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    installPrompt.value = e
  })
}

// Active course and enrolled courses state
const activeCourse = ref(null)
const enrolledCourses = ref([])
// Per-learner enrollment rows (course_enrollments) keyed by course_code.
// This is the real per-learner progress source — distinct from
// `enrolledCourses` above, which is actually the course catalogue (the
// name is historical).
const learnerEnrollments = ref(new Map())

const fetchLearnerEnrollments = async () => {
  if (!supabaseClient.value || !auth.learner.value?.id) {
    learnerEnrollments.value = new Map()
    return
  }
  try {
    const { data, error } = await supabaseClient.value
      .from('course_enrollments')
      .select('course_id, last_completed_lego_id, last_practiced_at, total_practice_minutes, current_cycle_index')
      .eq('learner_id', auth.learner.value.id)
    if (error) {
      console.warn('[App] fetchLearnerEnrollments failed:', error.message)
      return
    }
    const m = new Map()
    for (const r of data || []) m.set(r.course_id, r)
    learnerEnrollments.value = m
  } catch (err) {
    console.warn('[App] fetchLearnerEnrollments failed:', err)
  }
}

watch(() => auth.learner.value?.id, fetchLearnerEnrollments, { immediate: true })

// The course a `/learn/<code>` or `?course=` link opened this visit, if any
// (platform/learnLink). A learner who signs in or signs up on it keeps it: it
// becomes their saved course, so the next visit on any device lands there too
// rather than on whatever course their account last remembered.
const linkedCourseCode = ref(null)
watch(
  () => [auth.learner.value?.id, linkedCourseCode.value, activeCourse.value?.course_code],
  ([learnerId]) => {
    if (!learnerId || !supabaseClient.value) return
    const prefs = auth.learner.value?.preferences || {}
    const code = linkedCourseToSave(linkedCourseCode.value, activeCourse.value?.course_code, prefs.last_course_code)
    if (!code) return
    auth.learner.value.preferences = { ...prefs, last_course_code: code }
    supabaseClient.value
      .from('learners')
      .update({ preferences: { ...prefs, last_course_code: code } })
      .eq('id', learnerId)
      .then(({ error }) => {
        if (error) console.warn('[App] Failed to save linked course to DB:', error.message)
      })
  },
)
// True only when both URL and stored preference were absent — drives the
// auto-open of the Choose Course modal on first run.
const noPriorCourseSelection = ref(false)

// Course persistence key

// Handle course selection from CourseSelector.
//
// `origin` is how we got here, and it is stamped on the remembered course
// (platform/courseChoice): 'chosen' is somebody tapping a course, which is
// every caller but one. The org-enrolment picker passes 'default' when it
// closes without a tap and falls back to the org's first granted course —
// we picked that for the learner, and a course we picked is not a choice.
const handleCourseSelect = async (course, origin = 'chosen') => {
  const courseCode = course.course_code || course.id

  // IMPORTANT: Update courseDataProvider BEFORE activeCourse
  // This ensures LearningPlayer has the correct provider when it remounts
  // (activeCourse change triggers :key change which remounts the player)
  if (supabaseClient.value) {
    courseDataProvider.value = createCourseDataProvider({
      supabaseClient: supabaseClient.value,
      audioBaseUrl: config.s3.audioBaseUrl,
      courseId: courseCode,
    })
  }

  // Keep the warm-start cache honest for the new course (clears it on a
  // content_version bump). The full walk itself is LearningPlayer's deferred
  // handoff — not fired from here.
  if (supabaseClient.value) {
    checkCourseContentVersion(supabaseClient.value, courseCode)
  }

  // Signed in: the new course gets its own boot (job #315) — round map, resume
  // window, enrolment and the window's S3 clips in one request — BEFORE the
  // warm-up below, so the warm-up and the remounted player are answered from
  // it rather than walking round-map, cycles and the audio proxy in series.
  // Any course: the first window does not ride on the bundle cutover (index.html's
  // early boot asks it for every course); only the bundle key offer does.
  if (supabaseClient.value && auth.isAuthenticated.value) {
    restartSignedInBoot({
      course: courseCode,
      session: async () => {
        const { data } = await supabaseClient.value.auth.getSession()
        const sess = data.session
        return sess?.access_token && sess.user?.id ? { token: sess.access_token, userId: sess.user.id } : null
      },
      have: isBundleBootstrapEnabled(courseCode) ? (userId) => bootBundleHave(courseCode, userId) : undefined,
      intent: courseCode,
    })
  }

  // Warm the instant-playback caches (round-map + first-round cycles) BEFORE the
  // remount, so the new course's bootstrap is a cache hit instead of two cold
  // serial round-trips — the bulk of the ~1.7s course-switch cost. Fire-and-forget.
  void prewarmInstantCaches(courseCode)

  // NOW update activeCourse (triggers LearningPlayer remount via :key)
  activeCourse.value = course

  // Persist course selection (localStorage + DB), with how we arrived at it.
  rememberCourse(courseCode, origin)
  // Save to DB for cross-device persistence (fire-and-forget)
  if (supabaseClient.value && auth.learner.value?.id) {
    supabaseClient.value
      .from('learners')
      .update({ preferences: { ...auth.learner.value.preferences, last_course_code: courseCode } })
      .eq('id', auth.learner.value.id)
      .then(({ error }) => {
        if (error) console.warn('[App] Failed to save last course to DB:', error.message)
      })
  }
}

// The app's access verdict for a course (mirrors CourseSelector logic) — the
// same ONE resolver's answers (/api/entitlement/user + /api/subscription) as
// the server's content gate reads.
const courseAccessResult = (course) => {
  const { entitlements } = useSharedUserEntitlements()
  const { isSubscribed } = useSharedSubscription()
  const { platformRole } = useUserRole()
  const pricingTier = course.pricing_tier ?? inferPricingTier(course.target_lang ?? '', course.course_code)
  const isCommunity = course.is_community ?? course.course_code?.startsWith('community_')
  const devPaid = hasTryEntitlement()
  const subscription = {
    isActive: isSubscribed.value || devPaid,
    tier: (isSubscribed.value || devPaid) ? 'paid' : 'free',
  }
  return checkCourseAccess(
    { course_code: course.course_code, pricing_tier: pricingTier, is_community: isCommunity },
    subscription,
    entitlements.value,
    platformRole.value
  )
}

// Check if user can access a course
const canAccessCourse = (course) => {
  const result = courseAccessResult(course)
  // Premium courses are enterable/defaultable on preview — everyone can PLAY
  // every course through end-of-Yellow (seed 19). The seed-19 wall still gates
  // play via canAccessSeed in LearningPlayer; this only opens the door.
  return result.canAccess || result.canPreview
}

// Offline mirror of the courses catalogue. The catalogue query is the FIRST
// network dependency of every boot — without a course row, activeCourse stays
// null and the player never mounts, no matter how much audio/script is cached.
// So every successful fetch writes the rows here, and an offline/failed boot
// hydrates from this mirror instead of dead-ending (always-play invariant).
const CATALOGUE_CACHE_KEY = 'ssi-courses-catalogue-v1'
// The vintage the mirror above was written at, so a repeat boot can ask the
// ONE cheap question — "has the catalogue moved?" — instead of re-downloading
// it. Kept in its own key rather than wrapped around the rows, so a mirror
// written by an older build still reads (it simply has no stamp, takes one
// full fetch, and is stamped from then on).
const CATALOGUE_STAMP_KEY = 'ssi-courses-catalogue-stamp-v1'
const readCatalogueStamp = () => {
  try {
    return localStorage.getItem(CATALOGUE_STAMP_KEY) || null
  } catch (e) {
    return null
  }
}
const writeCatalogueStamp = (stamp) => {
  try {
    if (stamp) localStorage.setItem(CATALOGUE_STAMP_KEY, stamp)
    else localStorage.removeItem(CATALOGUE_STAMP_KEY)
  } catch (e) {
    // Storage full/blocked — next online boot just refetches.
  }
}
const readCatalogueCache = () => {
  try {
    const raw = localStorage.getItem(CATALOGUE_CACHE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null
  } catch (e) {
    return null
  }
}
const writeCatalogueCache = (rows) => {
  try {
    localStorage.setItem(CATALOGUE_CACHE_KEY, JSON.stringify(rows))
  } catch (e) {
    // Storage full/blocked — next online boot just refetches.
  }
}

// Fetch enrolled courses from Supabase
/**
 * Every column of `courses` EXCEPT the two that boot never reads.
 *
 * This query used to be `select('*')`, and `*` on this table is 1.74 MB of
 * JSON for 83 rows — because Popty stores its content-authoring working notes
 * on the same row. Measured live 2026-08-30:
 *
 *   quality_rules         1,364,495 bytes   78% of the payload   0 references in this repo
 *   translation_analysis    248,931 bytes   14% of the payload   0 references in this repo
 *   everything else         128,084 bytes    7%
 *
 * So 93% of the FIRST network dependency of every boot was authoring metadata
 * the player has never once looked at. On Fast 3G, with the JS bundle still
 * coming down the same pipe, its body took ~7s to arrive — the response
 * HEADERS landed at 2.28s, comfortably inside the 2500ms budget, and then the
 * body streamed past it. That is why a FIRST load hung and a REFRESH was fine:
 * on the second load the bundle is cached, the pipe is free, and the same
 * 1.5 MB lands in a few hundred ms. Measured against staging (no leash):
 * 5/5 first cold loads never rendered anything at all.
 *
 * Stated as an EXCLUSION rather than an allowlist on purpose. `enrolledCourses`
 * flows into the browse screen, the course switcher and LearningPlayer, and an
 * allowlist that missed a field would fail silently somewhere far from here.
 * Excluding two provably-unread columns cannot. A column added in Popty later
 * will need adding here — that is the accepted cost, and it is why the list is
 * next to the query rather than in a config file.
 */
const CATALOGUE_COLUMNS = [
  'audio_stamp', 'content_stamp', 'content_version', 'course_code', 'course_type',
  'created_at', 'creator_email', 'dialect', 'display_name', 'export_ready',
  'featured_order', 'gender_prep_check_notes', 'gender_prep_checked_at', 'is_community',
  'known_lang', 'learner_display_name', 'legacy_app_beta_started_at', 'legacy_app_status',
  'needs_gender_prep', 'new_app_beta_started_at', 'new_app_status', 'pricing_tier',
  'record_full_max_seed', 'released_at', 'seed_count', 'status', 'target_lang',
  'updated_at', 'variant_label', 'version', 'visibility', 'voice_config', 'voice_pool_key',
].join(',')

/**
 * The course catalogue read, as a real promise.
 *
 * Deliberately NOT session-scoped: the filter is `new_app_status`, and the
 * row set is identical for an anonymous and a service-role reader (verified
 * live 2026-08-30, 83 courses either way). Which is what makes it safe to
 * start before auth has resolved — only the SELECTION below needs the
 * learner, not the fetch.
 */
/**
 * index.html's early catalogue read (job #298), taken once. Resolves to
 * { data, error } like startCoursesQuery, so the boot below reads either alike.
 */
const takeEarlyCatalogue = () => {
  const early = typeof window !== 'undefined' ? window.__ssiEarlyCatalogue : null
  if (!early || typeof early.then !== 'function') return null
  window.__ssiEarlyCatalogue = undefined
  return early
}

const startCoursesQuery = (signal) => {
  // A first-word read (job #860): the player cannot name its course without it.
  let q = supabaseClient.value
    .from('courses')
    .select(CATALOGUE_COLUMNS)
    .in('new_app_status', ['live', 'beta'])
    .order('display_name')
  // Superseded attempts are aborted rather than left to hang, so a long wait
  // on a bad network cannot quietly accumulate dead sockets.
  if (signal) q = q.abortSignal(signal)
  return Promise.resolve(q)
}

/**
 * "Has the catalogue moved since we mirrored it?" — in about 200 bytes.
 *
 * The catalogue itself is 17.8 KB and used to go over the wire on EVERY load
 * including repeats, which made it the largest single thing on a repeat visit
 * (measured job #117, phone width on 4G). Nothing about it changes between two
 * visits a minute apart, so a repeat boot asks this instead, and only refetches
 * the rows when the answer differs from the stamp beside the mirror.
 *
 * The stamp is `<row count>:<newest updated_at>` over the same live/beta filter
 * the catalogue read uses. Both halves are needed and neither is enough alone:
 * editing a course moves its `updated_at`, so the newest one moves; publishing
 * or withdrawing one changes the COUNT even in the case where the row that left
 * was not the newest. Compared for equality, never for order — a withdrawal can
 * move the newest `updated_at` backwards, and that is still a change.
 */
const startCatalogueStampQuery = (signal) => {
  let q = supabaseClient.value
    .from('courses')
    .select('updated_at', { count: 'exact' })
    .in('new_app_status', ['live', 'beta'])
    .order('updated_at', { ascending: false })
    .limit(1)
  if (signal) q = q.abortSignal(signal)
  return Promise.resolve(q)
}

/**
 * Boot's head start on that read. `await auth.initialize()` costs ~600ms on a
 * cold boot and the catalogue fetch used to queue behind it, one after the
 * other, before any course could be named. Started alongside instead, claimed
 * once by the first fetchEnrolledCourses.
 */
let prefetchedCoursesQuery = null

/**
 * The same head start, for the cheap revalidation question. A boot that has a
 * stamped mirror starts THIS instead of the full catalogue read; only a stamp
 * that has moved (or no mirror at all) pays for the rows.
 */
let prefetchedCatalogueStampQuery = null

/**
 * Is the learner watching a blank screen because the catalogue has not landed?
 *
 * Only ever true on a FIRST cold visit — a returning learner has the offline
 * mirror and never gets here. Drives the one visible thing in the template.
 * Waiting visibly is a state; waiting blankly is the bug this closes.
 */
const catalogueSlow = ref(false)

const fetchEnrolledCourses = async () => {
  let data = null
  // Was `data` served by the offline mirror rather than fetched? A mirror does
  // not need re-writing, and must not be re-stamped with a vintage it was not
  // read at.
  let catalogueFromMirror = false
  // The live stamp, when the refetch below is happening BECAUSE it moved.
  let catalogueLiveStamp = null
  if (supabaseClient.value) {
    try {
      // Get courses available for this app (live or beta)
      // Status options: draft (hidden), beta (visible with badge), live (fully visible)
      // Bounded: supabase-js has no default request timeout, so on a weak
      // signal this select hangs and the app never picks an active course —
      // no course, no player, nothing plays, even with a full cache sitting
      // on the device. The offline catalogue mirror below is exactly the
      // fallback we want; it just has to be REACHED. (Tom 2026-08-15.)
      // ONE shared promise. It is awaited up to twice below, so it must be a
      // real promise rather than the Supabase thenable, which re-runs the
      // request every time it is awaited.
      //
      // Boot may already have this in flight (see startCoursesQuery) — claim
      // it once. Every later caller (PlayerContainer's refresh) finds the slot
      // empty and issues its own, so a refresh is never served stale.
      // LOCAL FIRST. A returning learner already holds every row of this
      // catalogue, stamped with the vintage it was written at. Ask the one
      // cheap question — "has it moved?" — and serve the mirror when it has
      // not. Refetching the rows is then what a MOVED stamp means, not what
      // every boot does. (Job #119; the 17.8 KB this skips was the biggest
      // thing on the wire on a repeat visit, measured job #117.)
      const mirroredRows = readCatalogueCache()
      const mirroredStamp = readCatalogueStamp()
      if (mirroredRows && mirroredStamp) {
        const stampQuery = prefetchedCatalogueStampQuery || startCatalogueStampQuery()
        prefetchedCatalogueStampQuery = null
        const stampRes = await withNetworkTimeout(stampQuery).catch(() => null)
        const liveStamp = catalogueStampOf(stampRes === NETWORK_TIMEOUT ? null : stampRes)
        if (decideCatalogueRead({ hasMirror: true, mirroredStamp, liveStamp }) === 'mirror') {
          // Unchanged, or unreachable — either way the mirror is what we have
          // and what we want. An unreachable stamp is the offline case, which
          // already served the mirror before this existed.
          catalogueFromMirror = true
          data = mirroredRows
        } else {
          console.log(`[App] Catalogue stamp moved (${mirroredStamp} → ${liveStamp}) — refetching the rows`)
          catalogueLiveStamp = liveStamp
        }
      }
      const coursesQuery = data
        ? null
        : (prefetchedCoursesQuery || startCoursesQuery())
      prefetchedCoursesQuery = null
      // supabase-js reports transport failures as `res.error` rather than
      // rejecting — but if it ever does reject, a first-time visitor must
      // still reach the waiter below instead of falling out to the catch and
      // dead-ending on a blank screen, which is the whole bug being closed.
      let res = coursesQuery === null ? null : await withNetworkTimeout(coursesQuery).catch((err) => {
        console.error('[App] Courses fetch threw:', err)
        return null
      })
      if (coursesQuery !== null && !usableCatalogue(res) && !readCatalogueCache()) {
        // The offline mirror is the right fallback for a RETURNING learner.
        // A FIRST-TIME visitor has never written one, so there is nothing here
        // to fall back TO — and giving up at a deadline would just trade a
        // blank screen that might still resolve for a blank screen that never
        // will. Neither is any use to the learner. So: say so on screen, and
        // keep trying until it lands. (Tom's ruling, 2026-08-30.)
        if (res === NETWORK_TIMEOUT) {
          console.warn('[App] Courses fetch exceeded its budget and there is no offline mirror to serve — telling the learner and staying on it.')
        } else if (res?.error) {
          console.error('[App] Failed to fetch courses:', res.error)
        }
        catalogueSlow.value = true
        res = await waitForCatalogue(coursesQuery, {
          startQuery: (signal) => startCoursesQuery(signal),
          onRetry: (n) => console.warn(`[App] No catalogue and no offline mirror — still trying (attempt ${n}).`),
        })
      }
      if (coursesQuery === null) {
        // Served from the mirror above — no rows were asked for.
      } else if (res === NETWORK_TIMEOUT) {
        console.warn('[App] Courses fetch exceeded its budget — falling back to the offline catalogue mirror.')
      } else if (res?.error) {
        console.error('[App] Failed to fetch courses:', res.error)
      } else {
        data = res?.data ?? null
      }
    } catch (err) {
      console.error('[App] Error fetching enrolled courses:', err)
    }
  }

  if (data && data.length > 0 && !catalogueFromMirror) {
    writeCatalogueCache(data)
    // Stamp the mirror with the vintage it was fetched at. When the refetch was
    // triggered BY a moved stamp we already hold that value; otherwise (first
    // ever fetch, or a mirror written by an older build) it is read off the
    // rows themselves — not a second lookup before the first word (job #860).
    writeCatalogueStamp(catalogueLiveStamp || catalogueStampOfRows(data))
  } else if (!data || data.length === 0) {
    // Offline / query failed — serve the last known catalogue so the saved
    // course resolves and the player boots into its cached-content paths.
    data = readCatalogueCache()
    if (data) console.log('[App] Courses catalogue hydrated from offline mirror:', data.length, 'courses')
  }
  // Either way the wait is over — the notice comes down.
  catalogueSlow.value = false

  try {
    // Set active course from: 1) localStorage, 2) first available
    if (data && data.length > 0) {
      // courses table now uses 'course_code' directly (renamed from 'code' 2026-01-18)
      enrolledCourses.value = data

      // Check for course from URL query parameter (e.g., ?course=spa_for_eng)
      let urlCourseCode = null
      // A Script DEMO link's course (job #987): the visitor is put on it for the
      // demo, but it is not remembered as their choice — their own course is
      // still theirs on the next visit.
      let scriptLinkCourse = null
      try {
        urlCourseCode = playCourseFromAddress(window.location.pathname, window.location.search)
        // urlCourseCode read from URL params
      } catch (e) {
        console.warn('[App] Failed to read URL params:', e)
      }

      // A SHARED SCRIPT-MODE LINK NAMES A LANGUAGE, NOT A COURSE (job #672).
      // One Script ladder per target language is a hard rule, so
      // `?script=ara&item=...` carries the language and script/scriptLanguages
      // resolves it to the course that language's ladder is read from — the
      // English-known one where there is one. Nothing here opens the overlay:
      // that is LearningPlayer's own read of the same param. This only puts
      // the visitor on a course whose ladder is the one they were sent.
      try {
        // Only a visit that actually carries `?script=` pays for the ladder
        // tables the resolution needs (job #763): everyone else's entry chunk
        // stays free of Script mode.
        const sharedScript = urlCourseCode ? null : parseScriptShare(window.location.search)
        if (sharedScript) {
          const { courseForSharedScript } = await import('./script/shareLink')
          const shared = courseForSharedScript(data, sharedScript)
          if (shared) { urlCourseCode = shared; scriptLinkCourse = sharedScript.itemId ? shared : null }
        }
      } catch (e) {
        console.warn('[App] Failed to resolve a shared Script link:', e)
      }

      // Check for saved course preference: DB (cross-device) then localStorage (fallback)
      // `savedOrigin` travels with it: the DB preference and the demo session
      // are only ever written by an explicit switch, but localStorage also
      // holds courses THIS code assigned, and the two must not read alike.
      let savedCourseCode = auth.learner.value?.preferences?.last_course_code || null
      let savedOrigin = savedCourseCode ? 'chosen' : null
      if (!savedCourseCode) {
        try {
          // Check demo session first, then persistent localStorage
          savedCourseCode = sessionStorage.getItem('ssi-demo-last-course')
          if (savedCourseCode) {
            savedOrigin = 'chosen'
          } else {
            const remembered = readRememberedCourse()
            savedCourseCode = remembered.code
            savedOrigin = remembered.origin
          }
        } catch (e) {
          console.warn('[App] Failed to read saved course:', e)
        }
      }

      // Priority: 1) URL param, 2) DB/localStorage, 3) first available
      let defaultCourse = null
      // How the course we end up on was arrived at — 'chosen' when the learner
      // pointed at it, 'default' when we picked for them, null when a legacy
      // stored value predates the marker. Written down beside the code, and
      // read by the prewarm gate below.
      let selectionOrigin = null

      // First try URL param — must pass the same entitlement gate as a
      // click, otherwise visiting /?course=<premium_code> bypasses the
      // paywall and the premium course just starts playing for a guest.
      if (urlCourseCode) {
        // A code the catalogue does not list is asked for once more, with the
        // visitor's own session: RLS hands a hidden course's row to an admin,
        // a dashboard user or an enrolled learner, and nobody else (job #192, #214:
        // the app checks that standing itself, because a draft row marked public is
        // returned to every visitor;
        // platform/hiddenCourseLink). Such a course plays for this visit only.
        const { platformRole: pRole } = useUserRole()
        // Enrolments load on a watch that may still be in flight on a cold
        // boot, so this course's enrolment is read here when it can change the answer.
        const mayPreview = await mayPreviewOutsideCatalogueAwaiting(urlCourseCode, data, {
          platformRole: pRole.value,
        }, async () => {
          const learnerId = auth.learner.value?.id
          if (!supabaseClient.value || !learnerId) return []
          // One course's enrolment, not the all-course list: the first word waits on it.
          const codes = await withNetworkTimeout(enrolledCodesForCourse(supabaseClient.value, learnerId, urlCourseCode)).catch(() => [])
          // A timeout resolves to a Symbol, not a rejection: no answer, so no enrolment.
          return codes === NETWORK_TIMEOUT ? [] : codes
        }, async () => {
          if (!supabaseClient.value) return false
          const yes = await withNetworkTimeout(isDashboardUserRpc(supabaseClient.value)).catch(() => false)
          return yes === true
        })
        const linked = await resolveLinkedCourse(urlCourseCode, data, async (code) => {
          const { data: row } = await withNetworkTimeout(Promise.resolve(
            courseRowQuery(supabaseClient.value, code, CATALOGUE_COLUMNS)))
            .catch(() => ({ data: null })) || {}
          return row || null
        }, mayPreview)
        const requested = linked?.course || null
        const previewOnly = linked?.source === 'preview'
        if (requested && canAccessCourse(requested)) {
          defaultCourse = requested
          // A `?course=` link IS the visitor naming their destination.
          selectionOrigin = 'chosen'
          if (previewOnly) {
            // Not remembered and not saved as their course: their next visit
            // without the link opens their own course, not a draft.
            scriptLinkCourse = urlCourseCode
            console.log('[App] Course:', urlCourseCode, 'opened by link outside the catalogue (preview, not remembered)')
          } else if (urlCourseCode !== scriptLinkCourse) {
            rememberCourse(urlCourseCode, 'chosen')
            linkedCourseCode.value = urlCourseCode
          }
        } else if (!requested) {
          // A course link naming a course the catalogue does not hold (a typo,
          // a retired code): the learner is offered the picker rather than
          // dropped silently into some other course (platform/learnLink).
          if (unknownCourseLanding(savedCourseCode) === 'first-picker') {
            // A stranger: the first-load picker PAGE, with no course assigned
            // behind it. Before this, /learn/eng_for_xyzzy mounted Afrikaans
            // under the picker and remembered it for the next visit (red-team
            // #133, finding 11). pickerFirstActive also keeps the default-
            // course fallback below from assigning one.
            pickerFirstActive.value = true
            void router.isReady().then(() => router.replace({ path: PICKER_FIRST_PATH }))
          } else {
            requestCoursePicker()
            // After the router settles: the first navigation waits on the
            // player's chunk, and a replace before it would be overwritten.
            void router.isReady().then(() => {
              if (route.query.course !== urlCourseCode) return
              const { course: _unknown, ...rest } = route.query
              return router.replace({ query: rest, hash: route.hash })
            })
          }
        }
        // If the URL-requested course is locked, fall through to the other
        // resolution paths — leave defaultCourse null here. We can't push
        // to /premium from this async boot path without racing the router.
      }

      // Then try localStorage (but only if user can access it)
      if (!defaultCourse && savedCourseCode) {
        const saved = data.find(c => c.course_code === savedCourseCode)
        if (saved && canAccessCourse(saved)) {
          defaultCourse = saved
          // Carry the stored origin forward rather than laundering it into a
          // choice: a course we assigned on visit one is still not a choice on
          // visit two, however many times it is read back.
          selectionOrigin = savedOrigin
        }
      }
      // Fall back to the first course the user can actually access. If
      // there isn't one (rare — would mean every course in the catalogue
      // is gated for this user), leave defaultCourse null so no premium
      // course gets auto-loaded; the CourseSelector picker will open.
      // A stranger on the first-load picker (job #784, platform/pickerFirst)
      // is choosing right now: no course is assigned behind their back, and
      // nothing is fetched for one. Their tap assigns it (handleCourseSelect).
      if (!defaultCourse && pickerFirstActive.value) {
        console.log('[App] Course: none yet — the stranger is on the picker')
      } else if (!defaultCourse) {
        // What a stranger sees in the first three seconds. This used to prefer
        // a hardcoded Chinese course; a first open in India with no referrer
        // landed there. The decision, and why, lives in the module.
        defaultCourse = pickFirstOpenDefaultCourse(data, canAccessCourse)
        noPriorCourseSelection.value = true
        selectionOrigin = 'default'
      }

      if (defaultCourse && !activeCourse.value) {
        activeCourse.value = {
          ...defaultCourse,
          completedRounds: 0,
          progress: 0,
          lastSession: null,
        }
        // Create courseDataProvider for the default course
        courseDataProvider.value = createCourseDataProvider({
          supabaseClient: supabaseClient.value,
          audioBaseUrl: config.s3.audioBaseUrl,
          courseId: defaultCourse.course_code,
        })
        // Remember this course for next visit — WITH how we got to it, so an
        // auto-assigned default never reads back as a learner's preference.
        // A legacy stored value with no origin is left exactly as it is:
        // re-stamping it would be inventing a fact about the past.
        if (selectionOrigin && defaultCourse.course_code !== scriptLinkCourse) rememberCourse(defaultCourse.course_code, selectionOrigin)
        console.log('[App] Course:', defaultCourse.course_code, `(${selectionOrigin || 'origin unknown'})`)

        // Keep the warm-start cache honest before the player mounts. The full
        // walk itself is LearningPlayer's deferred handoff — not fired here.
        checkCourseContentVersion(supabaseClient.value, defaultCourse.course_code)

        // Warm the instant-playback caches for a course the learner is
        // actually headed to — a `?course=` link, a class, a remembered
        // choice. NOT for one we assigned ourselves: that spent a round-map
        // and first-cycles round trip of the default course's audio on every
        // enrol, redeem, join and try-link visit by somebody here for
        // something else (job #596). The visitor who does then stay on the
        // default pays a cold first play, which is the right person to charge.
        // Fire-and-forget; mirrors the handleCourseSelect wiring above.
        if (selectionOrigin !== 'default') {
          void prewarmInstantCaches(defaultCourse.course_code)
        }
      }
    }
  } catch (err) {
    console.error('[App] Error resolving active course:', err)
  }
}

// Provide stores and state to child components
// The family-management surface (mounted below) — opened either by Settings
// or by the Paddle success redirect for the Family plan.
const { isOpen: familyModalOpen, open: openFamilyModal, close: closeFamilyModal } = useFamilyModal()

provide('progressStore', progressStore)
provide('sessionStore', sessionStore)
provide('courseDataProvider', courseDataProvider)
provide('auth', auth)
// One shell covers learner, admin, Intelligence and schools. Production removes this import.
if (typeof __STAFF_CLICK_STAGING__ !== 'undefined' && __STAFF_CLICK_STAGING__) {
  let stopClicks = null
  let generation = 0
  watch(() => [auth.userId.value, auth.learner.value?.platform_role, auth.learner.value?.educational_role], async ([userId, role, education]) => {
    const current = ++generation
    stopClicks?.(); stopClicks = null
    if (!userId || !supabaseClient.value || (role !== 'ssi_admin' && education !== 'god')) return
    const { startStaffClicks } = await import('./telemetry/staffClicks')
    if (current === generation) stopClicks = startStaffClicks(supabaseClient.value, router)
  }, { immediate: true })
}

provide('supabase', supabaseClient)
// Which game sent this person in (job #513): stored the first time they are signed in.
watchGameAttribution(auth.isAuthenticated, async () => (await supabaseClient.value?.auth.getSession())?.data?.session?.access_token ?? null)
// Counting asks no question (Tom, 2026-10-15; composables/countingConsent.ts): an old No becomes an objection, dead keys go.
tidyCountingKeys()
// The campaign funnel's sign-up and paid steps for a tab that arrived from a campaign (job #702).
watchCampaignFunnel(auth.isAuthenticated, async () => (await supabaseClient.value?.auth.getSession())?.data?.session?.access_token ?? null)
// A tab that came by a /go/ link or a friend's share: its sign-in is one step of that visit (job #703).
watchGoSignIn(auth.isAuthenticated, async () => (await supabaseClient.value?.auth.getSession())?.data?.session?.access_token ?? null)
provide('config', config)
provide('activeCourse', activeCourse)
provide('enrolledCourses', enrolledCourses)
provide('learnerEnrollments', learnerEnrollments)
provide('fetchLearnerEnrollments', fetchLearnerEnrollments)
provide('noPriorCourseSelection', noPriorCourseSelection)
provide('handleCourseSelect', handleCourseSelect)
provide('theme', { theme, toggleTheme, setTheme })
provide('eagerScript', eagerScript)
provide('inviteCode', inviteCode)
provide('installPrompt', installPrompt)
provide('fetchEnrolledCourses', fetchEnrolledCourses)

// Rehydrate an in-flight admin view-as (sessionStorage) after a reload, and
// arm the read-only guard that makes view-as unable to write (no-op when off).
const { restoreViewAs, applyViewAsParam } = useViewAs()
installViewAsFetchGuard()

onMounted(async () => {
  // The framed demo mounts its own view and nothing else — see IS_EMBED above.
  if (IS_EMBED) return

  // PWA install signal (job #783): launch-standalone flag + appinstalled, once per
  // page load (App mounts once; route changes never re-run this). The 5s flush
  // timer lets sign-in settle so the row is attributed.
  try { startInstallTelemetry(installLog.event) } catch { /* telemetry never blocks boot */ }
  // Store renewals and purchases cut off mid-flow re-verify through the server
  // (plan 032). A no-op on the web and in any shell where store billing is off.
  listenForStoreTransactions()

  // Clear stale caches on new deploy
  const previousBuild = invalidateStaleCaches()

  // Re-prime the schools context if an admin reloaded while viewing-as.
  restoreViewAs().catch(err => {
    console.warn('[App] view-as restore failed (non-fatal):', err)
  })
  // ?viewAs=<user id> from a click-through link (admin-only; ignored for anyone else).
  applyViewAsParam().catch(err => {
    console.warn('[App] ?viewAs apply failed (non-fatal):', err)
  })

  // Check service worker kill switch (for emergency recovery)
  // If kill switch is active, this will unregister SW and reload
  checkKillSwitch().catch(err => {
    console.warn('[App] Kill switch check failed (non-fatal):', err)
  })

  // Ask, once, whether the live build is newer than this one — and if it
  // provably is, say so on screen and take it, rather than updating silently
  // under the learner (Tom, 2026-09-18). Deliberately NOT awaited: when there
  // is no update, and whenever we are offline, this costs boot nothing and
  // paints nothing at all.
  //
  // First, say how the last update went if this load IS its restart ("Updated
  // to <build>", or honestly that it came back on the old one). Then register
  // the service worker and keep checking on resume and while open — the whole
  // lifecycle, and why it used to need a force-quit, is in useAppUpdate.ts.
  confirmAfterReload(previousBuild)
  void runOpenUpdateGate()
  startUpdateWatch()

  // Supabase client was created synchronously above. Finish the async parts
  // (stores + auth init) now that mount is complete.
  if (supabaseClient.value) {
    try {
      progressStore.value = createProgressStore({ client: supabaseClient.value })
      sessionStore.value = createSessionStore({ client: supabaseClient.value })

      // Catalogue read goes out NOW, in parallel with auth init, rather than
      // waiting its turn behind it. Errors are handled where it is awaited.
      if (readCatalogueCache() && readCatalogueStamp()) {
        prefetchedCatalogueStampQuery = startCatalogueStampQuery()
        void prefetchedCatalogueStampQuery.catch(() => {})
      } else {
        // index.html asked for it already, before this code had arrived (job #298).
        prefetchedCoursesQuery = takeEarlyCatalogue() ?? startCoursesQuery()
        void prefetchedCoursesQuery.catch(() => {})
      }

      // Initialize auth with Supabase client (for learner management). Its
      // completion — including the "staff on a fresh browser lands on the
      // dashboard, not the bare player" redirect that used to live here — is
      // now owned by the shared resolved-session gate (useResolvedSession),
      // written by useAuth and read by router/index.ts's one-shot corrective
      // redirect. One mechanism, reachable from router guards too, instead of
      // this component-local check.
      if (auth) {
        // Bounded. auth.initialize() times out its own getSession(), but the
        // learner-row read and guest-progress migration behind it were
        // unbounded, so on a weak signal this await could hold boot
        // indefinitely — and everything below it, including the course
        // catalogue the player needs to mount at all. A learner whose auth
        // has not resolved still gets to play what is cached; the session
        // settles behind them and the UI is reactive to it.
        // (Tom 2026-08-15: "never as a gate".)
        // observe: false — the learner-row read inside auth.initialize() is
        // bounded and observed by useAuth itself; bounding it again here must
        // not count the same slow read as a second failure (2026-09-24).
        const authResult = await withNetworkTimeout(auth.initialize(supabaseClient.value), undefined, { observe: false })
        if (authResult === NETWORK_TIMEOUT) {
          console.warn('[App] Auth init exceeded its budget — booting into cached content; session will settle behind us.')
        }
      }

      // Initialize entitlements + subscription (now that supabase + auth are
      // ready) WITHOUT blocking boot on their round-trips (1-2s each on a cold
      // deployment). Nothing at boot needs the fresh values: default-course
      // picking is identical either way — canAccessCourse returns
      // canAccess||canPreview and EVERY live/beta course is at least
      // previewable, so entitlements can't change the pick — and the seed-19
      // wall (canAccessSeed) first fires well into play, long after these
      // land. Both composables also hydrate from their localStorage cache at
      // setup for instant reads.
      const { initialize: initEntitlements } = useSharedUserEntitlements()
      const { initialize: initSubscription } = useSharedSubscription()
      const entitlementsReady = Promise.all([initEntitlements(), initSubscription()]).catch(() => {})

      // ONE rule for every way in (join link, code, OTP, password, sign-out):
      // when the identity changes, both answers are void for the new person —
      // anything held that was not given for them is dropped, every ask still
      // in flight is superseded (a generation counter, so account A's late
      // reply can never land on B), the answers read "not yet in" (gated UI
      // waits: no padlock, no paid content) and both are asked again.
      // Registered after the boot fetch so a restored session is not fetched twice.
      {
        const shared = useSharedUserEntitlements()
        const sharedSub = useSharedSubscription()
        watch(() => auth.userId.value, (id, prev) => {
          if (id === prev) return
          void Promise.all([shared.switchIdentity(id ?? null), sharedSub.switchIdentity(id ?? null)]).catch(() => {})
        })
      }

      // THE VERDICT HAS LANDED: sweep the bundle cache against it (job #778).
      // A same-owner full bundle is only valid while its owner is still
      // entitled; the sweep re-asks the server for any course whose stored
      // declaration the verdict now contradicts, and the server slices it.
      // Silent, background, once per (course, identity, verdict); a lapse
      // that lands later in the session (the clock crossing a period end, or
      // a refreshed answer) re-runs it through the watch. Never tears down a
      // running player — the next materialisation picks the change up.
      // After the first sound (job #290): the sweep reads every stored course
      // copy back from disk, whole courses each, which is boot-path weight.
      void Promise.all([entitlementsReady, firstSound()]).then(() => revalidateCachedBundles().catch(() => {}))
      watch(
        () => useSharedSubscription().isSubscribed.value,
        () => { void revalidateCachedBundles().catch(() => {}) },
      )

      // THE MOMENT AFTER SOMEBODY PAYS. Pick up any purchase that was paid
      // for and has not yet landed — written down before Paddle's success
      // redirect, so it is still here on the other side of the page load, and
      // still here after a phone tears a backgrounded PWA down. Until it
      // resolves the app shows the waiting state rather than the
      // not-subscribed state, which is the whole of Tom's "looks like nothing
      // has happened, then spins endlessly" (2026-09-07). Chained off the
      // subscription init so the first check reads a hydrated answer.
      // Read the URL NOW, before the family-landing handler below strips its
      // flags off it — both handlers hang off the same promise and neither
      // should depend on the other's ordering.
      const bootSearch = window.location.search
      void entitlementsReady.then(() => {
        try {
          usePendingPurchase().resume(bootSearch)
        } catch (e) {
          console.warn('[App] Pending-purchase resume failed (non-fatal):', e)
        }
      })

      // WHERE A FAMILY PAYER LANDS. Paddle's success redirect for the Family
      // plan carries ?family=1, and initSubscription already polls until the
      // activating webhook has landed (just_subscribed=1). So the moment the
      // subscription is real, open the surface that lets them add their
      // family — rather than dropping them on a course list with no sign the
      // six seats they just bought exist (Tom, 2026-09-07).
      void entitlementsReady.then(() => {
        try {
          const params = new URLSearchParams(window.location.search)
          if (params.get('family') !== '1') return
          // Clean the flag off the URL so a refresh doesn't reopen it.
          const url = new URL(window.location.href)
          url.searchParams.delete('family')
          url.searchParams.delete('just_subscribed')
          history.replaceState(null, '', url.toString())
          if (useSharedSubscription().isSubscribed.value) openFamilyModal()
        } catch (e) {
          console.warn('[App] Family landing check failed (non-fatal):', e)
        }
      })

      // 30-day offline lease (the "Spotify handshake"). Wire boot/reconnect/timer
      // renewals AFTER subscription resolves, so the first renew sees the
      // freshest entitlement state — chained off the promise rather than
      // blocking boot. Idempotent + best-effort (fail-open offline).
      void entitlementsReady.then(() => {
        try {
          useOfflineLease().initialize(supabaseClient, auth.userId)
        } catch (e) {
          console.warn('[App] Offline-lease init failed (non-fatal):', e)
        }
      })

      // Claim any email-allowlist (pre-granted) free access for a restored /
      // already-signed-in session — onAuthStateChange's SIGNED_IN doesn't fire
      // for a session restored on load, so this covers returning users.
      // Fire-and-forget: it's a no-op for everyone without a pending grant,
      // and when a grant DOES land it refreshes entitlements itself, so the
      // UI unlocks reactively. Awaiting it put a full /api/access/claim
      // round-trip (1s+ on a cold deployment) on every boot's critical path.
      if (auth.learner.value) {
        void (async () => {
          try {
            const { data: { session } } = await supabaseClient.value.auth.getSession()
            if (session?.access_token) {
              await useAccessClaim().claimAccess(session.access_token)
            }
          } catch (e) {
            console.warn('[App] Access claim failed (non-fatal):', e)
          }
        })()
      }

      // INTENT, second signal: the learner's own saved course. On a fresh
      // device there is nothing in localStorage, so the synchronous warm-up
      // above had no name to work with and the download otherwise waits for
      // the course list and enrollment reads to finish. De-duped — if the
      // boot warm-up already started this course, this joins that fetch.
      // Only when the visit named no course (job #287): a learner opening
      // Chinese whose saved course is Spanish downloaded the whole Spanish
      // bundle (2.3 MB gzipped) beside the Chinese one on every first open.
      if (!bootIntendedCourse) warmBundleForIntent(auth.learner.value?.preferences?.last_course_code)

      // Handle ?code= URL parameter for invite codes
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const codeParam = urlParams.get('code')
        if (codeParam) {
          // Clean code from URL immediately
          const cleanUrl = new URL(window.location.href)
          cleanUrl.searchParams.delete('code')
          history.replaceState(null, '', cleanUrl.toString())

          // Validate the code
          const valid = await inviteCode.validateCode(codeParam)
          if (valid) {
            // If entitlement code grants specific courses, inject as ?course= so fetchEnrolledCourses selects it
            const granted = inviteCode.pendingCode.value?.grantedCourses
            if (granted?.length) {
              const url = new URL(window.location.href)
              if (!url.searchParams.has('course')) {
                url.searchParams.set('course', granted[0])
                history.replaceState(null, '', url.toString())
              }
            }
            if (auth.learner.value) {
              // Already signed in — redeem immediately
              try {
                const { data: { session } } = await supabaseClient.value.auth.getSession()
                if (session?.access_token) {
                  const result = await inviteCode.redeemCode(session.access_token)
                  if (result.success) {
                    await Promise.all([useSharedUserEntitlements().revalidate(), useSharedSubscription().revalidate()])
                  }
                }
              } catch (e) {
                console.warn('[App] Auto-redeem failed:', e)
              }
            } else if (!isRedeemFlowRoute.value) {
              // Not signed in — the ONE join flow (job #895): /redeem/:code,
              // the same screen every invite link opens (name, email, in),
              // never the separate email-code modal. Skipped while /redeem or
              // /group is itself mounted: that page already owns this code.
              void router.replace({ name: 'redeem-code', params: { code: codeParam } })
            }
          }
        }
      } catch (err) {
        console.warn('[App] Failed to process invite code from URL:', err)
      }

      // A pending code from sessionStorage (a join left half-way) and nobody
      // signed in: back to the one join screen for it, not the modal (job #895)
      // — unless /redeem or /group is itself currently mounted (see above).
      if (!auth.learner.value && inviteCode.pendingCode.value && !isRedeemFlowRoute.value) {
        void router.replace({ name: 'redeem-code', params: { code: inviteCode.pendingCode.value.code } })
      }

      // Fetch enrolled courses
      await fetchEnrolledCourses()
    } catch (err) {
      console.error('[App] Failed to initialize Supabase:', err)
    }
  } else {
    // Running in demo mode (database not configured or disabled)
  }
})
</script>

<template>
  <div class="app-root">
    <RoutedView />
    <!-- The framed demo is the view and nothing else: no escape pill, no PWA
         update prompt (it never registers a service worker to update), no
         install banner, no sign-in or checkout overlays. See IS_EMBED. -->
    <template v-if="!IS_EMBED">
    <!-- The player's bottom nav on every learner page off the player (job #46,
         Tom 2026-09-24): routes opt in with meta.learnerNav. -->
    <RoutedBottomNav v-if="showLearnerNav" />
    <AppEscape v-if="showAppEscape" />
    <AppEscape v-else-if="dashboardEscape" :to="dashboardEscape" />
    <!-- School staff playing on their OWN account: the live warning sits on
         the player, the only place anything is playing (job #683). -->
    <PlayingAsYourselfBanner v-if="route.name === 'player'" />
    <!-- ONE status pill (job #831). While the first navigation to the player
         is still fetching its code it says so instead of a blank screen (job
         #784), in full rather than typed — a typewriter caught mid-word read
         "Getting your phrases read" on Tom's iPhone — and that loading line
         replaces any version note rather than stacking a second banner. -->
    <PwaUpdatePrompt :loading="bootingToPlayer || bootingToPicker ? bootDialogLine : ''" />
    <UpdateOnOpenOverlay />
    <!-- PLAY copies only: the test-switch chip, since the surface has no address bar (job #496). -->
    <PlaySwitchesChip v-if="showPlaySwitches" />
    <AccountContestPrompt :client="supabaseClient" />
    <!-- A temporary password stands in front of everything until the person chooses their own (job #236). -->
    <MustChangePassword :client="supabaseClient" />
    <InstallBanner v-if="overlaysMounted" />
    <!-- A sign-in the server would not link to the person's own account says so
         in words, instead of leaving them on an empty account (plan 028). -->
    <ClaimRefusedBanner v-if="overlaysMounted" />
    <GrantProofPrompt v-if="overlaysMounted" />
    <ViewingAsBanner v-if="overlaysMounted || viewingAsSomeone" />
    <WalkOverlay v-if="activeWalk" />
    <PlanPicker v-if="overlaysMounted" />
    <CheckoutOverlay v-if="overlaysMounted" />
    <ParentAskOverlay v-if="overlaysMounted" />
    <PurchasePendingOverlay v-if="overlaysMounted || pendingPurchaseOverlay" />
    <StoreConfirmingOverlay v-if="overlaysMounted" />
    <!-- Where a Family payer lands. One instance, two doors: the Paddle
         success redirect (?family=1) and Settings → Manage family. -->
    <FamilyManagementModal v-if="overlaysMounted || familyModalOpen" :is-open="familyModalOpen" @close="closeFamilyModal" />
    <InAppBrowser v-if="overlaysMounted" />
    <!--
      Shown only on a FIRST cold visit whose catalogue has not arrived yet.
      A returning learner has the offline mirror and never sees this. It is not
      an error state and offers nothing to tap: the app is still trying, and
      the one useful thing it can do is say so rather than sit blank.
    -->
    <div v-if="catalogueSlow" class="slow-network-notice">
      <div class="slow-network-card">
        <div class="slow-network-pulse" aria-hidden="true"></div>
        <p class="slow-network-title">Still loading</p>
        <p class="slow-network-body">
          Your connection looks slow. This will start on its own as soon as it
          comes through, so there's no need to reload.
        </p>
      </div>
    </div>
    <div v-if="killSwitchMessage" class="kill-switch-overlay">
      <p>{{ killSwitchMessage }}</p>
    </div>
    </template>
  </div>
</template>

<style>
/*
 * All design tokens now live in styles/design-tokens.css.
 * This block is intentionally empty — base styles are in style.css,
 * global utilities in styles/global.css.
 */
</style>

<style scoped>
.app-root {
  /* Same as the schools shell: the view-as band pads the body down, so the
     root's full-height floor comes down by the band's height (job #675). */
  min-height: calc(100vh - var(--top-bands-h, 0px));
  min-height: calc(100dvh - var(--top-bands-h, 0px));
  background: var(--bg-primary);
}

/*
 * The slow-network notice. Mist tokens, no belt accent — this is the shell
 * speaking before any course exists, so there is no belt colour to carry yet.
 * Edge-anchored and full-bleed, so it pads itself out of the notch and the
 * home indicator per the standing safe-area rule.
 */
.slow-network-notice {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(24px, env(safe-area-inset-top, 0px)) max(24px, env(safe-area-inset-right, 0px))
    max(24px, env(safe-area-inset-bottom, 0px)) max(24px, env(safe-area-inset-left, 0px));
  background: var(--bg-primary);
}

.slow-network-card {
  max-width: 320px;
  text-align: center;
}

.slow-network-pulse {
  width: 10px;
  height: 10px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: var(--text-secondary, #6b6560);
  animation: slow-network-breathe 1.8s ease-in-out infinite;
}

@keyframes slow-network-breathe {
  0%, 100% { opacity: 0.25; transform: scale(0.85); }
  50% { opacity: 0.9; transform: scale(1.15); }
}

@media (prefers-reduced-motion: reduce) {
  .slow-network-pulse { animation: none; opacity: 0.6; }
}

.slow-network-title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 600;
  color: var(--text-primary, #2b2724);
}

.slow-network-body {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text-secondary, #6b6560);
}

.kill-switch-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  background: rgba(20, 16, 14, 0.92);
  color: #fff;
}

.kill-switch-overlay p {
  max-width: 420px;
  font-size: 16px;
  line-height: 1.5;
}
</style>
